import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { MatInput } from '@angular/material/input';
import { TranslateService } from '@ngx-translate/core';
import { IChoosenFilterInterface } from 'app/library/all-users/interfaces/choosen-filter.interface';
import { TextFilterEntry } from 'app/library/all-users/interfaces/extended-attribute-filer.interface';
import { AllUsersApiService } from 'app/library/all-users/services/all-users.api-service';
import { ExtendedAttribute } from 'app/library/extended-attributes/models/extended-attribute';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { takeUntil } from 'rxjs/operators';
import { BUILTIN_COURSE_EXECUTION_TEXT_FIELDS, BuiltInCourseExecutionTextFieldKey } from './built-in-course-execution-text-fields';
import { BUILTIN_USER_TEXT_FIELDS, BuiltInUserTextFieldKey } from './built-in-user-text-fields';

@Component({
    selector: 'app-text-filter',
    templateUrl: './text-filter.component.html',
    styleUrls: ['./text-filter.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class TextFilterComponent extends UnsubscribeOnDestroy implements OnInit, OnChanges {
    @ViewChild(MatInput)
    private _valueInput: MatInput;

    @Input()
    userAttribute: ExtendedAttribute | null = null;

    @Input()
    builtInUserTextField: BuiltInUserTextFieldKey | null = null;

    @Input()
    builtInCourseExecutionTextField: BuiltInCourseExecutionTextFieldKey | null = null;

    @Output()
    addedToList = new EventEmitter<IChoosenFilterInterface[]>();

    list: IChoosenFilterInterface[] = [];
    inputValue = '';
    filterMode: 'like' | 'eq' = 'like';

    constructor(private _allUsersService: AllUsersApiService, private _translateService: TranslateService) {
        super();
    }

    ngOnInit(): void {
        this._allUsersService.filtersList$.pipe(takeUntil(this._unsubscribeAll)).subscribe((filters) => {
            this.list = filters;
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.userAttribute || changes.builtInUserTextField || changes.builtInCourseExecutionTextField) {
            this.inputValue = '';
            this.filterMode = 'like';
        }
    }

    get titleTranslationKey(): string | null {
        if (this.builtInUserTextField) {
            return BUILTIN_USER_TEXT_FIELDS[this.builtInUserTextField].translationKey;
        }
        if (this.builtInCourseExecutionTextField) {
            return BUILTIN_COURSE_EXECUTION_TEXT_FIELDS[this.builtInCourseExecutionTextField].translationKey;
        }
        return this.userAttribute?.translationKey ?? null;
    }

    get hasAddableText(): boolean {
        return !!(this.inputValue || '').trim();
    }

    onFilterModeChange(): void {
        this._focusValueInput();
    }

    getModeLabel(mode: 'like' | 'eq'): string {
        return mode === 'like'
            ? this._translateService.instant('GENERAL.LABEL.CONTAINS')
            : this._translateService.instant('GENERAL.LABEL.IS_EXACTLY');
    }

    addToList(): void {
        if (!this.builtInUserTextField && !this.builtInCourseExecutionTextField && !this.userAttribute) {
            return;
        }

        const value = (this.inputValue || '').trim();
        if (!value) {
            return;
        }

        const fieldId = this._fieldId();
        const entityType = this.builtInUserTextField
            ? 'UserProfileTextFilter'
            : this.builtInCourseExecutionTextField
            ? 'CourseExecutionTextFilter'
            : 'ExtendedAttribute';
        const attributeName = this._attributeName();

        const existing = this.list.find((item) => item.entity.id === fieldId && item.entity.type === entityType);
        const textFilters: TextFilterEntry[] = [...(existing?.textFilters ?? [])];

        if (textFilters.some((f) => f.value === value)) {
            this._focusValueInput();
            return;
        }

        textFilters.push({ value, mode: this.filterMode });

        const attrLabel = this._translateService.instant(
            this.builtInUserTextField
                ? BUILTIN_USER_TEXT_FIELDS[this.builtInUserTextField].translationKey
                : this.builtInCourseExecutionTextField
                ? BUILTIN_COURSE_EXECUTION_TEXT_FIELDS[this.builtInCourseExecutionTextField].translationKey
                : this.userAttribute!.translationKey
        );
        const valuesDisplay = textFilters
            .map((tag) => `${tag.value} (${this.getModeLabel(tag.mode)})`)
            .join(', ');

        const entry: IChoosenFilterInterface = {
            entity: { id: fieldId, type: entityType },
            valueToShow: `${attrLabel}: ${valuesDisplay}`,
            attributeName,
            attributeId: fieldId,
            textFilters,
        };

        this.addedToList.emit([
            ...this.list.filter((item) => !(item.entity.id === fieldId && item.entity.type === entityType)),
            entry,
        ]);

        this.inputValue = '';
        this._focusValueInput();
    }

    private _fieldId(): string {
        if (this.builtInUserTextField) {
            return BUILTIN_USER_TEXT_FIELDS[this.builtInUserTextField].id;
        }
        if (this.builtInCourseExecutionTextField) {
            return BUILTIN_COURSE_EXECUTION_TEXT_FIELDS[this.builtInCourseExecutionTextField].id;
        }
        return this.userAttribute!.id;
    }

    private _attributeName(): string {
        if (this.builtInUserTextField) {
            return BUILTIN_USER_TEXT_FIELDS[this.builtInUserTextField].attributeName;
        }
        if (this.builtInCourseExecutionTextField) {
            return BUILTIN_COURSE_EXECUTION_TEXT_FIELDS[this.builtInCourseExecutionTextField].attributeName;
        }
        return this.userAttribute!.name;
    }

    private _focusValueInput(): void {
        setTimeout(() => this._valueInput?.focus());
    }
}
